import React, { useEffect, useRef } from 'react';
import { AdvancedSearch } from '../AdvancedSearch';
import { FmlxButton, FmlxDivider } from 'fmlx-common-ui';
import { SearchResultPanel } from './SearchResultPanel';
import { CloseOutlineIcon, FastSettingIcon, SearchIcon } from '@fmlx/icons';
import styles from './BasicSearch.module.scss';
import { SearchBarTargetChips } from './SearchBarTargetChips';
import { useSearchBarContext } from '../Context/SearchBarContext';
import { ESearchBarMode, ESearchBarStatus } from '../Context/types';
import '../SearchBar.scss';

interface BasicSearchProps {
  onSearch: () => void;
}

export const BasicSearch = (props: BasicSearchProps) => {
  const {
    filters,
    updateFilters,
    mode,
    setMode,
    isPanelOpen: isOpen,
    setIsPanelOpen: setIsOpen,
    openAdvancedSearch,
    closeAdvancedSearch,
    clearSearch,
    status,
    explorerStatus,
  } = useSearchBarContext();
  const hasSearchResults =
    status !== ESearchBarStatus.Empty || explorerStatus !== ESearchBarStatus.Empty;
  const showAdvanced = mode === ESearchBarMode.Advanced;
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        (target as Element).closest?.(
          '.MuiPopover-root, .MuiPopper-root, [data-portal-root]',
        )
      ) {
        return;
      }
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        if (mode === ESearchBarMode.Advanced) {
          closeAdvancedSearch();
        } else {
          setMode(ESearchBarMode.Basic);
        }
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mode, setIsOpen, setMode, closeAdvancedSearch]);

  const handleAdvancedSearchButton = () => {
    setIsOpen(!showAdvanced);
    if (showAdvanced) {
      closeAdvancedSearch();
    } else {
      openAdvancedSearch();
    }
  };

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateFilters({ query: e.target.value, fileName: e.target.value });
  };

  return (
    <div id="basic-search-wrapper" ref={wrapperRef} className={styles.searchWrapper}>
      <div
        id="basic-search-bar"
        className={`${styles.searchBar} ${
          isOpen && !showAdvanced ? styles.searchBarExpanded : ''
        }`}
      >
        <div id="basic-search-main-row" className={styles.searchBarMainRow}>
          <SearchIcon className={styles.searchIcon} />
          <input
            id="basic-search-input"
            type="text"
            placeholder="Search"
            className={styles.searchInput}
            value={filters.query}
            onFocus={() => setIsOpen(true)}
            onClick={() => setIsOpen(true)}
            onChange={onSearchInputChange}
            disabled={showAdvanced}
          />
          {hasSearchResults && (
            <FmlxButton
              id="basic-search-clear-button"
              className={styles.advancedSearchButton}
              variant={FmlxButton.Variant.PLAIN}
              type={FmlxButton.Type.BASIC}
              onlyIcon
              icon={<CloseOutlineIcon />}
              withTooltip={false}
              onClick={clearSearch}
            />
          )}
          <FmlxButton
            id="basic-search-advanced-button"
            className={styles.advancedSearchButton}
            variant={FmlxButton.Variant.PLAIN}
            type={FmlxButton.Type.BASIC}
            onlyIcon
            icon={<FastSettingIcon />}
            withTooltip={false}
            onClick={handleAdvancedSearchButton}
          />
        </div>

        {!showAdvanced && (
          <>
            <div id="basic-search-target-chips-row" className={styles.searchBarButtonRow}>
              <SearchBarTargetChips />
            </div>
            <div id="basic-search-divider" className="SearchDivider">
              <FmlxDivider variant={FmlxDivider.Variant.MIDDLE} />
            </div>
            <div id="basic-search-result-section" className={styles.searchResultSection}>
              <SearchResultPanel onSearch={props.onSearch} />
            </div>
          </>
        )}
      </div>

      {showAdvanced && (
        <div id="basic-search-extension" className={styles.searchExtension}>
          <AdvancedSearch onSearchClick={props.onSearch} />
        </div>
      )}
    </div>
  );
};
